import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {(() => void) | null} */
  #mainDrawerTopCleanup = null;

  /** @type {number | null} */
  #drawerTopScrollRaf = null;

  /** Prevents duplicate close work (e.g. Escape) while the close animation runs. */
  #mainDrawerClosePending = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupDrawerLanguagePicker();
    this.#setupMobileMenuDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#stopMainDrawerTopSync();
  }

  /**
   * Mobile drawer uses fixed top = bottom of header-component. That value can be wrong if the
   * USP row / fonts / images finish loading after the first measure — keep it updated while open.
   */
  #syncMainDrawerTop = () => {
    const headerComponent = this.closest('header-component');
    if (!(headerComponent instanceof HTMLElement)) return;
    const bottom = Math.round(headerComponent.getBoundingClientRect().bottom);
    this.style.setProperty('--drawer-dynamic-top', `${Math.max(0, bottom)}px`);
  };

  #startMainDrawerTopSync = () => {
    this.#stopMainDrawerTopSync();
    this.#syncMainDrawerTop();
    requestAnimationFrame(() => {
      this.#syncMainDrawerTop();
      requestAnimationFrame(() => this.#syncMainDrawerTop());
    });

    const syncIfOpen = () => {
      if (this.refs.details?.hasAttribute('open')) this.#syncMainDrawerTop();
    };

    const observers = [];
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) {
      const ro = new ResizeObserver(syncIfOpen);
      ro.observe(headerGroup);
      observers.push(ro);
    }

    const onResize = () => syncIfOpen();
    window.addEventListener('resize', onResize);

    const onScroll = () => {
      if (this.#drawerTopScrollRaf !== null) return;
      this.#drawerTopScrollRaf = requestAnimationFrame(() => {
        this.#drawerTopScrollRaf = null;
        syncIfOpen();
      });
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });

    const lateLayoutTimer = window.setTimeout(syncIfOpen, 400);

    this.#mainDrawerTopCleanup = () => {
      window.clearTimeout(lateLayoutTimer);
      observers.forEach((o) => o.disconnect());
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (this.#drawerTopScrollRaf !== null) {
        cancelAnimationFrame(this.#drawerTopScrollRaf);
        this.#drawerTopScrollRaf = null;
      }
    };
  };

  #stopMainDrawerTopSync = () => {
    this.#mainDrawerTopCleanup?.();
    this.#mainDrawerTopCleanup = null;
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getMainMenuPanel() {
    return this.refs.details?.querySelector(':scope > [data-header-drawer]') ?? null;
  }

  /**
   * Mobile panel uses <dialog> for semantics; we use show() (not showModal) so the browser does not
   * block the menu trigger — <summary> lives outside the dialog and must stay clickable.
   */
  #setupMobileMenuDialog() {
    const panel = this.#getMainMenuPanel();
    if (!(panel instanceof HTMLDialogElement)) return;

    panel.addEventListener('click', (event) => {
      if (event.target === panel) {
        this.close();
      }
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   * Only runs when the click was on the drawer trigger (hamburger), not on a menu item summary inside the drawer.
   * Prevents native summary toggle so JS controls open state (avoids double-toggle).
   * @param {Event} [event]
   */
  toggle(event) {
    const triggerSummary = this.refs.details?.querySelector(':scope > summary');
    const realTarget = event?.composedPath?.()?.[0] ?? event?.target;
    const clickedTrigger =
      triggerSummary &&
      realTarget instanceof Element &&
      (realTarget === triggerSummary || triggerSummary.contains(realTarget));
    if (!clickedTrigger) {
      return;
    }
    if (event?.target?.closest?.('summary')) {
      event.preventDefault();
    }
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (event?.target instanceof Element && event.target.closest('summary')) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      this.#startMainDrawerTopSync();
    }

    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      const mainPanel = this.#getMainMenuPanel();
      if (details === this.refs.details && mainPanel instanceof HTMLDialogElement && !mainPanel.open) {
        mainPanel.show();
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details) {
      if (this.#mainDrawerClosePending) return;
      this.#mainDrawerClosePending = true;
      this.#stopMainDrawerTopSync();
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (details === this.refs.details) {
          const mainPanel = this.#getMainMenuPanel();
          if (mainPanel instanceof HTMLDialogElement && mainPanel.open) {
            mainPanel.close();
          }
          this.#mainDrawerClosePending = false;
        }
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Drawer language picker: on option click set select value and submit form (dropdown opens above)
   */
  #setupDrawerLanguagePicker() {
    const picker = this.querySelector('[data-drawer-language-picker]');
    if (!picker) return;
    const form = picker.closest('form');
    const select = picker.querySelector('select');
    const buttons = picker.querySelectorAll('.drawer-language-picker__option');
    if (!form || !select) return;
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.getAttribute('data-value');
        if (value) {
          select.value = value;
          form.submit();
        }
      });
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
